const asNumber = (value) => {
  const result = Number(value);
  return Number.isFinite(result) ? result : null;
};

const getFields = (profile) => {
  if (Array.isArray(profile)) return profile;
  let fields = profile?.data ?? profile ?? {};
  if (fields && !Array.isArray(fields) && Array.isArray(fields.data)) {
    fields = fields.data;
  }
  return fields;
};

const readField = (profile, names) => {
  const fields = getFields(profile);
  if (Array.isArray(fields)) {
    const field = fields.find((item) =>
      names.includes(item?.uniqueId ?? item?.name),
    );
    if (!field) return null;
    const nested = field.value;
    const value =
      nested && typeof nested === "object" && !Array.isArray(nested)
        ? nested.value ?? nested.amount ?? nested
        : nested;
    return {
      name: field.uniqueId ?? field.name,
      value,
      unit: field.unit ?? field.units ?? nested?.unit ?? nested?.units,
    };
  }

  for (const name of names) {
    const value = fields?.[name] ?? profile?.[name];
    if (value !== undefined && value !== null && value !== "") {
      return {
        name,
        value,
        unit: fields?.[`${name}_unit`] ?? profile?.[`${name}_unit`],
      };
    }
  }
  return null;
};

const normalizeGender = (profile, fallback) => {
  const raw =
    readField(profile, ["gender", "sex"])?.value ??
    fallback?.gender ??
    fallback?.sex;
  const value = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (["female", "f", "woman", "0"].includes(value)) return 0;
  if (["male", "m", "man", "1"].includes(value)) return 1;
  return null;
};

const ageFromBirthYear = (profile, fallback) => {
  const birthYear = asNumber(
    readField(profile, ["birth_year", "birthYear"])?.value ??
      fallback?.birth_year ??
      fallback?.birthYear,
  );
  if (birthYear === null || birthYear < 1900 || birthYear > 2100) return null;

  const birthMonth = asNumber(
    readField(profile, ["birth_month", "birthMonth"])?.value ??
      fallback?.birth_month ??
      fallback?.birthMonth,
  );
  const now = new Date();
  const birthdayPending =
    birthMonth !== null &&
    birthMonth >= 1 &&
    birthMonth <= 12 &&
    now.getMonth() + 1 < birthMonth;
  const age = now.getFullYear() - birthYear - (birthdayPending ? 1 : 0);
  return age > 0 && age <= 120 ? age : null;
};

const normalizeAge = (profile, fallback) => {
  const direct = asNumber(
    readField(profile, ["age", "age_years", "ageYears"])?.value ??
      fallback?.age,
  );
  if (direct !== null && direct > 0 && direct <= 120) return Math.floor(direct);

  const fromBirthYear = ageFromBirthYear(profile, fallback);
  if (fromBirthYear !== null) return fromBirthYear;

  const dob =
    readField(profile, ["date_of_birth", "dateOfBirth", "dob", "birthDate"])
      ?.value ??
    fallback?.dateOfBirth ??
    fallback?.dob;
  if (!dob) return null;
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return null;
  const now = new Date();
  let age = now.getUTCFullYear() - birthDate.getUTCFullYear();
  const beforeBirthday =
    now.getUTCMonth() < birthDate.getUTCMonth() ||
    (now.getUTCMonth() === birthDate.getUTCMonth() &&
      now.getUTCDate() < birthDate.getUTCDate());
  if (beforeBirthday) age -= 1;
  return age > 0 && age <= 120 ? age : null;
};

const isImperialUnit = (unit, pounds) =>
  pounds
    ? ["lb", "lbs", "pound", "pounds"].includes(unit)
    : ["in", "inch", "inches"].includes(unit);

const isMetricUnit = (unit, pounds) =>
  pounds
    ? ["kg", "kilogram", "kilograms"].includes(unit)
    : ["cm", "centimeter", "centimeters"].includes(unit);

const convertMeasurement = ({
  field,
  storedMetricNames,
  imperialNames,
  min,
  max,
  pounds,
}) => {
  if (!field) return null;
  let value = asNumber(field.value);
  if (value === null) return null;

  const unit = String(field.unit ?? "")
    .trim()
    .toLowerCase();

  // v4 widgets attach a display unit (en → in/lb, ja → cm/kg) even though
  // uniqueId "height" / "weight" are always stored as cm / kg.
  if (storedMetricNames.includes(field.name)) {
    return value >= min && value <= max ? Math.round(value) : null;
  }

  if (imperialNames.includes(field.name) || isImperialUnit(unit, pounds)) {
    value *= pounds ? 0.45359237 : 2.54;
  } else if (!isMetricUnit(unit, pounds)) {
    return null;
  }

  return value >= min && value <= max ? Math.round(value) : null;
};

const normalizeBpGroup = (profile, fallback) => {
  const raw =
    readField(profile, [
      "bp_group",
      "bpGroup",
      "blood_pressure_group",
      "bloodPressureGroup",
      "hypertension_history",
    ])?.value ?? fallback?.bpGroup;
  if ([0, 1, 2].includes(Number(raw))) return Number(raw);
  const value = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (["normal", "no_hypertension"].includes(value)) return 0;
  if (["pre_hypertension", "prehypertension", "elevated"].includes(value))
    return 1;
  if (["hypertension", "hypertensive", "yes"].includes(value)) return 2;
  return 0;
};

const readMeasurementField = (profile, fallbackUser, names, fallbackKeys) => {
  const fromProfile = readField(profile, names);
  if (fromProfile) return fromProfile;
  for (const key of fallbackKeys) {
    const value = fallbackUser?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return { name: key, value, unit: fallbackUser?.[`${key}_unit`] };
    }
  }
  return null;
};

export const buildVitalsSubjectInfo = (profile, fallbackUser = {}) => {
  const height = readMeasurementField(
    profile,
    fallbackUser,
    ["height_cm", "heightCm", "height_in", "height"],
    ["height_cm", "heightCm", "height_in", "height"],
  );
  const weight = readMeasurementField(
    profile,
    fallbackUser,
    ["weight_kg", "weightKg", "weight_lb", "weight_lbs", "weight"],
    ["weight_kg", "weightKg", "weight_lb", "weight_lbs", "weight"],
  );
  const subjectInfo = {
    gender: normalizeGender(profile, fallbackUser),
    ageYears: normalizeAge(profile, fallbackUser),
    heightCm: convertMeasurement({
      field: height,
      storedMetricNames: ["height_cm", "heightCm", "height"],
      imperialNames: ["height_in"],
      min: 50,
      max: 250,
      pounds: false,
    }),
    weightKg: convertMeasurement({
      field: weight,
      storedMetricNames: ["weight_kg", "weightKg", "weight"],
      imperialNames: ["weight_lb", "weight_lbs"],
      min: 20,
      max: 300,
      pounds: true,
    }),
    bpGroup: normalizeBpGroup(profile, fallbackUser),
  };

  const labels = {
    gender: "gender",
    ageYears: "age or date of birth",
    heightCm: "height",
    weightKg: "weight",
  };
  const missingFields = Object.entries(subjectInfo)
    .filter(([key, value]) => key !== "bpGroup" && value === null)
    .map(([key]) => labels[key]);

  return { subjectInfo, missingFields, valid: missingFields.length === 0 };
};
